import assign from 'object-assign';
import Firebase from 'firebase';
import isFunction from 'lodash/isFunction';
import keys from 'object-keys';
import memoize from 'lodash/memoize';
import noop from 'lodash/noop';
import normalizeUrl from 'normalize-url';
import now from 'date-now';
import trim from 'trim';
import values from 'lodash/values';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromEventPattern';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/never';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/mergeMapTo';
import 'rxjs/add/operator/publishReplay';
import 'rxjs/add/operator/share';










/**
 * Creates an instance for monitoring live viewer counts for a given channel id.
 *
 * @class  Eyeball
 */
export class Eyeball {

    static VERSION = process.env.EYEBALL_VERSION;



    /**
     * Global Eyeball settings.
     *
     * @property {boolean} debug            - Set to true to enable debug logging in the console
     * @property {number}  presenceInterval - The heartbeat interval in milliseconds to update the client presence
     * @property {number}  sinceAmount      - The offset amount in milliseconds to count active viewers with
     * @property {number}  throttleAmount   - The delay amount in milliseconds to throttle values being sent to the subscriber
     *
     * @type {Object}
     * @static true
     * @public
     */
    static settings = {
        debug: false,
        presenceInterval: 900000, // 15 minutes
        sinceAmount: 18000000, // 5 hours
        throttleAmount: 15000 // 15 seconds
    };



    /**
     * Default instance options
     *
     * @property {boolean} presence - Option to enable/disable populating a client presence for the instance
     *
     * @type {Object}
     * @static true
     * @private
     */
    static defaultOptions = {
        presence: true
    };



    /**
     * The Firebase app instance.
     *
     * @type {Firebase}
     * @static true
     * @private
     */
    static _ref = new Firebase('https://adultswimeye.firebaseio.com');



    /**
     * Creates an observable of a child object in the database representing the client
     *
     * @param {string} channel - The channel id.
     *
     * @static true
     * @return {Observable}
     */
    static createPresence$(channel) {
        return Observable.create(observer => {
            let presence = Eyeball._ref.child(channel).push();
            presence.onDisconnect().set(null);
            observer.next(presence);
            return () => {
                presence.onDisconnect().cancel();
                presence.set(null);
                presence = null;
            };
        });
    }



    /**
     * Creates an observable stream of channel updates.
     *
     * @param {string} channel - The channel id
     *
     * @static true
     * @return {Observable}
     */
    static getViewers$(channel) {
        return _getMemoizedViewers$(channel);
    }



    /**
     * Reduces the active viewers to just a number, only emits when the value changes.
     *
     * @param {string} channel - This channel id
     *
     * @static true
     * @return {Observable}
     */
    static getViewerCount$(channel) {
        return Eyeball.getViewers$(channel)
            .map(viewers => viewers.length)
            .distinctUntilChanged();
    }


    /**
     * Composes an observable that maintains the presence and viewer count for a channel. The return is cached and shared with other eyeball instances on the same channel.
     *
     * @param {string} channel - The channel id
     * @param {Object} [options] - An optional options object from the Eyeball instance
     *
     * @static true
     * @return {ConnectableObservable}
     */
    static getWatch$(channel, options = Eyeball.defaultOptions) {
        return _getMemoizedWatch$.call(this, channel, options);
    }



    /**
     * Debug logger, Eyeball.settings.debug must be set to `true`.
     *
     * @param {string} label - A label for the message
     * @param {...*}   data  - Any number of arguments to log after the label
     *
     * @static true
     */
    static log(label, ...data) {
        if (Eyeball.settings.debug) {
            let date = new Date();
            let hours = date.getHours();
                hours = hours < 10 ? '0' + hours : hours;
            let minutes = date.getMinutes();
                minutes = minutes < 10 ? '0' + minutes : minutes;
            let timestamp = `${hours}:${minutes}`;
            console.log.apply(console, [`👀 ${timestamp}${label ? ` [${label}]`: ''}`, ...data]);
        }
    }



    /**
     * Creates an observable stream for managing the client's presence in a channel.
     *
     * @param {string} channel - The channel id
     *
     * @static true
     * @return {Observable}
     */
    static setPresence$(channel) {
        let timer$ = Observable.timer(0, Eyeball.settings.presenceInterval);
        return Eyeball.createPresence$(channel)
            .combineLatest(connection$, timer$, (presence, status) => {
                presence.transaction(currentData => (status ?
                    assign({ created: Firebase.ServerValue.TIMESTAMP }, currentData, {
                        at: Firebase.ServerValue.TIMESTAMP,
                        referrer: normalizeUrl(`${window.location.host}${window.location.pathname}/`)
                    }) : null
                ), (err, committed, snap) => {
                    const key = snap.key();
                    if (err) Eyeball.log(channel, 'presence error:', key, err);
                    else if (!committed) Eyeball.log(channel, 'presence not committed:', key);
                    else Eyeball.log(channel, 'presence:', key, snap.val());
                }, false);
            })
            // Don't actually emit values
            .mergeMapTo(Observable.never());
    }














    /**
     * The constructor. Optionally pass a channel id and handlers for next/error/complete events to start watching.
     * @constructor
     *
     * @param {string} [channel] - The channel id to initially watch, leave blank to not start watching.
     * @param {Object} [options={}] - The next value observable event handler
     */
    constructor(channel, options = {}) {
        this._channel = '';
        this._options = assign({}, Eyeball.defaultOptions, options);
        this._subscriptions = [];
        this._$ = null;
        try {
            // Ignore watch errors on init
            this.watch(channel, options.onNext, options.onError, options.onComplete);
        } catch(err) {}
        return this;
    }



    /**
     * Cleans up and removes all subscriptions for the instance.
     *
     * @returns {Eyeball} The eyeball instance.
     */
    destroy () {
        this.unsubscribeAll();
        this._subcriptions = [];
        this._$ = null;
        this._channel = '';
        return this;
    }



    /**
     * Method for adding additional subscriptions.
     * Calling watch() to change channels will automatically unsubscribe.
     *
     * @param {Function|Observable} onNext     - The next value observable event handler
     * @param {Function}            onError    - The error observable event handler
     * @param {Function}            onComplete - The complete observable event handler
     *
     * @returns {Subscriber} The resulting subscriber object with an unsubscribe() method for manually unsubscribing. This will also automatically be unsubscribed when changing channels via watch() method.
     */
    subscribe (onNext, onError, onComplete) {
        if (!this._$) throw new Error('Not watching any channel');
        const subscription = this._$.subscribe(onNext, onError, onComplete);
        this._subscriptions.push(subscription);
        return subscription;
    }



    /**
     * Method for subscribing to the raw viewer data.
     * Next values will be an array of objects each with a `key`, `at`, and `created` property.
     * Calling watch() to change channels will automatically unsubscribe.
     *
     * @param {Function|Observable} onNext     - The next value observable event handler
     * @param {Function}            onError    - The error observable event handler
     * @param {Function}            onComplete - The complete observable event handler
     *
     * @returns {Subscriber} The resulting subscriber object with an unsubscribe() method for manually unsubscribing. This will also automatically be unsubscribed when changing channels via watch() method.
     */
    subscribeRaw (onNext, onError, onComplete) {
        if (!this._$) throw new Error('Not watching any channel');
        const subscription = Eyeball.getViewers$(this._channel)
            .subscribe(onNext, onError, onComplete);
        this._subscriptions.push(subscription);
        return subscription;
    }



    /**
     * Loops through and unsubscribes all subscriptions.
     *
     * @public
     * @returns {Eyeball} The eyeball instance.
     */
    unsubscribeAll() {
        this._subscriptions.forEach(sub => {
            if (sub && isFunction(sub.unsubscribe)) sub.unsubscribe();
        });
        return this;
    }



    /**
     * Removes any previous channel subscriptions and presences and opens up a new one for the provided channel id.
     *
     * @param {string}              [channel]    - The channel id to watch.
     * @param {Function|Observable} [onNext]     - The next value observable event handler
     * @param {Function}            [onError]    - The error observable event handler
     * @param {Function}            [onComplete] - The complete observable event handler
     *
     * @returns {Eyeball} The eyeball instance.
     */
    watch (channel = this._channel, onNext, onError, onComplete) {
        let id = trim(channel);

        if (!id) throw new Error('Invalid channel id');
        if (id === this._channel) return this;

        this.unsubscribeAll();
        this._subscriptions = [];
        this._channel = id;
        this._$ = Eyeball.getWatch$(id, this._options);

        this.subscribe(
            (x)   => Eyeball.log(this._channel, 'next:', x),
            (err) => Eyeball.log(this._channel, 'error:', err),
            ()    => Eyeball.log(this._channel, 'complete')
        );

        if (isFunction(onNext) || isFunction(onError) || isFunction(onComplete)) {
            this.subscribe(
                isFunction(onNext) ? onNext : noop,
                isFunction(onError) ? onError : noop,
                isFunction(onComplete) ? onComplete : noop
            );
        }
        return this;
    }


}










/**
 * An observable to listen to connection updates.
 *
 * @private
 * @owner Eyeball
 * @type {ConnectableObservable}
 */
const connectionRef = Eyeball._ref.child('/.info/connected');
const connection$ = Observable.fromEventPattern(
        fn => connectionRef.on('value', fn),
        fn => connectionRef.off('value', fn),
        snap => snap.val())
    .distinctUntilChanged()
    .do(connected => Eyeball.log('Connection Status', connected))
    .publishReplay(1);

Eyeball._connection$ = connection$;
connection$.connect();










/**
 * An observable to get the server time offset for comparing with the client's local time.
 *
 * @private
 * @owner Eyeball
 * @type {ConnectableObservable}
 */
const offsetRef = Eyeball._ref.child('/.info/serverTimeOffset');
const offset$ = connection$.mergeMap(connected => Observable.fromEventPattern(
        fn => connected ? offsetRef.once('value', fn) : noop,
        fn => offsetRef.off('value', fn),
        snap => snap.val()))
    .do(offset => Eyeball.log('Server Offset', offset))
    .publishReplay(1);

Eyeball._offset$ = offset$;
offset$.connect();










/**
 * Creates a cached observable of viewers for sharing between multiple Eyeball instances.
 *
 * @param {string} channel - The channel id
 *
 * @owner Eyeball
 * @return {ConnectableObservable}
 */
const _getMemoizedViewers$ = memoize((channel) => {
    let timer$ = Observable.timer(0, Eyeball.settings.throttleAmount);
    return offset$
        .combineLatest(timer$, offset => Eyeball._ref.child(channel)
            .orderByChild('at')
            .startAt(now() + offset - Eyeball.settings.sinceAmount))
        .mergeMap(ref => Observable.fromEventPattern(
            fn => ref.once('value', fn),
            fn => ref.off('value', fn),
            snap => values(snap.val() || {})))
        .do(viewers => Eyeball.log(channel, 'value:', keys(viewers).length))
        .share();
});










/**
 * Creates a cached observable composed of a viewer count and a channel presence for sharing between multiple Eyeball instances.
 *
 * @param {string} channel - The channel id
 *
 * @owner Eyeball
 * @return {ConnectableObservable}
 */
const _getMemoizedWatch$ = memoize((channel, options = Eyeball.defaultOptions) => {
    let watch$ = Observable.merge(
        Eyeball.getViewerCount$(channel),
        (options.presence) ? Eyeball.setPresence$(channel) : Observable.never());
    return watch$.share();
}, (c, o) => `#${c}#${JSON.stringify(o)}`);










// Allow messing with Firebase directly in the console, etc. if in dev
if (process.env.NODE_ENV === 'development') {
    window.Eyeball = Eyeball;
    window.Firebase = Firebase;
}










module.exports = Eyeball;



/** WEBPACK FOOTER **
 ** ./src/Eyeball.js
 **/